import React, { useState, useMemo } from 'react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  ChevronRightIcon,
  FunnelIcon,
  ChevronLeftIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

/**
 * EventListView - List view for calendar events
 *
 * Features:
 * - Filterable by event type (Appointments, Meetings, Tasks, Reminders)
 * - Sortable by date
 * - Groups events by date
 * - Pagination with 10, 25, or View All options
 * - Click to open event slideout
 * - Responsive design
 */
const EventListView = ({
  events = [],
  loading = false,
  onEventClick,
  onToggleTaskStatus, // Callback to toggle task completion
  categoryFilters = {}
}) => {
  // Event type filter state
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');

  // Task status filter (only shown when Tasks filter is active)
  const [taskStatusFilter, setTaskStatusFilter] = useState('open'); // 'open' | 'closed' | 'all'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 'all', label: 'View All' }
  ];

  // Event type definitions
  const eventTypes = [
    { key: 'all', label: 'All', color: 'gray' },
    { key: 'shift', label: 'Shifts', color: 'orange' },
    { key: 'appointment', label: 'Appointments', color: 'blue' },
    { key: 'meeting', label: 'Meetings', color: 'purple' },
    { key: 'task', label: 'Tasks', color: 'amber' },
    { key: 'reminder', label: 'Reminders', color: 'green' }
  ];

  // Helper to check if task is closed (completed or cancelled)
  const isTaskClosed = (event) => {
    return event.eventType === 'task' && ['completed', 'cancelled'].includes(event.status);
  };

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let filtered = events;

    // Apply category filters (external/internal/personal)
    filtered = filtered.filter(event => categoryFilters[event.category] !== false);

    // Apply event type filter
    if (activeTypeFilter !== 'all') {
      filtered = filtered.filter(event => event.eventType === activeTypeFilter);
    }

    // Apply task status filter (only when viewing tasks)
    if (activeTypeFilter === 'task' && taskStatusFilter !== 'all') {
      filtered = filtered.filter(event => {
        const isClosed = isTaskClosed(event);
        return taskStatusFilter === 'closed' ? isClosed : !isClosed;
      });
    }

    // Sort by start time (ascending)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.startTime);
      const dateB = new Date(b.startTime);
      return dateA - dateB;
    });
  }, [events, categoryFilters, activeTypeFilter, taskStatusFilter]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTypeFilter, categoryFilters, taskStatusFilter]);

  // Reset task status filter when switching away from tasks
  React.useEffect(() => {
    if (activeTypeFilter !== 'task') {
      setTaskStatusFilter('open');
    }
  }, [activeTypeFilter]);

  // Pagination calculations
  const totalEvents = filteredEvents.length;
  const isViewAll = itemsPerPage === 'all';
  const totalPages = isViewAll ? 1 : Math.ceil(totalEvents / itemsPerPage);

  // Get paginated events
  const paginatedEvents = useMemo(() => {
    if (isViewAll) return filteredEvents;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredEvents.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEvents, currentPage, itemsPerPage, isViewAll]);

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Group paginated events by date
  const groupedEvents = useMemo(() => {
    const groups = {};

    paginatedEvents.forEach(event => {
      const dateKey = format(parseISO(event.startTime), 'yyyy-MM-dd');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });

    return groups;
  }, [paginatedEvents]);

  // Get date label (Today, Tomorrow, or formatted date)
  const getDateLabel = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  // Get event type styling
  const getEventTypeStyle = (eventType) => {
    const styles = {
      shift: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
      appointment: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
      meeting: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
      task: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
      reminder: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
      time_block: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' }
    };
    return styles[eventType] || styles.task;
  };

  // Get status badge (non-overdue statuses)
  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return { label: 'Completed', bg: 'bg-green-100', text: 'text-green-700' };
    }
    if (status === 'cancelled') {
      return { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-500' };
    }
    if (status === 'in_progress') {
      return { label: 'In Progress', bg: 'bg-blue-100', text: 'text-blue-700' };
    }
    return null;
  };

  // Check if event is overdue (only for tasks and reminders)
  const isEventOverdue = (event) => {
    // Only tasks and reminders can be overdue
    if (event.eventType !== 'task' && event.eventType !== 'reminder') {
      return false;
    }
    const eventDate = parseISO(event.startTime);
    return isPast(eventDate) && event.status === 'scheduled';
  };

  // Count events by type for filter badges
  const eventCounts = useMemo(() => {
    const counts = { all: events.length };
    events.forEach(event => {
      counts[event.eventType] = (counts[event.eventType] || 0) + 1;
    });
    return counts;
  }, [events]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <span className="ml-3 text-gray-500">Loading events...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filter Tabs */}
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            {eventTypes.map((type) => {
              const count = eventCounts[type.key] || 0;
              const isActive = activeTypeFilter === type.key;

              return (
                <button
                  key={type.key}
                  onClick={() => setActiveTypeFilter(type.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors flex-shrink-0 ${
                    isActive
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Task Status Toggle - only shown when Tasks filter is active */}
          {activeTypeFilter === 'task' && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5 flex-shrink-0">
              <button
                onClick={() => setTaskStatusFilter('open')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  taskStatusFilter === 'open'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                Open
              </button>
              <button
                onClick={() => setTaskStatusFilter('closed')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  taskStatusFilter === 'closed'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <CheckCircleSolidIcon className="h-3.5 w-3.5 text-green-500" />
                Closed
              </button>
              <button
                onClick={() => setTaskStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  taskStatusFilter === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Event List */}
      <div className="divide-y divide-gray-100">
        {Object.keys(groupedEvents).length === 0 ? (
          <div className="p-8 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTypeFilter !== 'all'
                ? `No ${activeTypeFilter}s in the selected date range.`
                : 'No events in the selected date range.'}
            </p>
          </div>
        ) : (
          Object.entries(groupedEvents).map(([dateKey, dateEvents]) => (
            <div key={dateKey}>
              {/* Date Header */}
              <div className="bg-gray-50 px-4 py-2 sticky top-0 z-10">
                <h3 className="text-sm font-semibold text-gray-900">
                  {getDateLabel(dateKey)}
                </h3>
              </div>

              {/* Events for this date */}
              <div className="divide-y divide-gray-100">
                {dateEvents.map((event) => {
                  const typeStyle = getEventTypeStyle(event.eventType);
                  // Don't show "Completed" badge for tasks - checkmark + strikethrough already indicate status
                  const shouldHideCompletedBadge = event.eventType === 'task' && event.status === 'completed';
                  const statusBadge = shouldHideCompletedBadge ? null : getStatusBadge(event.status);
                  const overdue = isEventOverdue(event);
                  const startDate = parseISO(event.startTime);
                  const endDate = parseISO(event.endTime);

                  return (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className="w-full text-left px-4 py-4 md:px-6 md:py-4 hover:bg-gray-50 transition-colors group"
                    >
                      {/* Desktop/Tablet: Grid layout with labeled columns */}
                      <div className={`hidden md:grid md:grid-cols-12 md:gap-4 items-center ${isTaskClosed(event) ? 'opacity-60' : ''}`}>
                        {/* Color indicator / Task checkbox */}
                        <div className="col-span-1 flex justify-center">
                          {event.eventType === 'task' ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleTaskStatus) {
                                  onToggleTaskStatus(event.id, isTaskClosed(event) ? 'scheduled' : 'completed');
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (onToggleTaskStatus) {
                                    onToggleTaskStatus(event.id, isTaskClosed(event) ? 'scheduled' : 'completed');
                                  }
                                }
                              }}
                              className="p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                              title={isTaskClosed(event) ? 'Reopen task' : 'Complete task'}
                            >
                              {isTaskClosed(event) ? (
                                <CheckCircleSolidIcon className="h-6 w-6 text-green-500" />
                              ) : (
                                <CheckCircleIcon className="h-6 w-6 text-gray-400 hover:text-green-500" />
                              )}
                            </div>
                          ) : (
                            <div className={`w-1.5 h-14 rounded-full ${typeStyle.dot}`} />
                          )}
                        </div>

                        {/* Time column */}
                        <div className="col-span-2">
                          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Time</p>
                          {event.allDay ? (
                            <p className="text-sm font-medium text-gray-900">All day</p>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-900">{format(startDate, 'h:mm a')}</p>
                              <p className="text-xs text-gray-500">{format(endDate, 'h:mm a')}</p>
                            </>
                          )}
                        </div>

                        {/* Title & Type column */}
                        <div className="col-span-4">
                          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                            Event <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${typeStyle.bg} ${typeStyle.text} normal-case`}>
                              {event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}
                            </span>
                            {overdue && (
                              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 normal-case">
                                Overdue
                              </span>
                            )}
                          </p>
                          <h4 className={`text-sm font-medium text-gray-900 truncate ${isTaskClosed(event) ? 'line-through text-gray-500' : ''}`}>
                            {event.title}
                          </h4>
                          {statusBadge && (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          )}
                        </div>

                        {/* Customer column */}
                        <div className="col-span-2">
                          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Customer</p>
                          {event.customerName ? (
                            <p className="text-sm text-gray-700 truncate">{event.customerName}</p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">—</p>
                          )}
                        </div>

                        {/* Location column */}
                        <div className="col-span-2">
                          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Location</p>
                          {event.locationAddress ? (
                            <p className="text-sm text-gray-700 truncate">{event.locationAddress}</p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">—</p>
                          )}
                        </div>

                        {/* Chevron */}
                        <div className="col-span-1 flex justify-end">
                          <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                        </div>
                      </div>

                      {/* Mobile: Compact vertical layout */}
                      <div className={`md:hidden flex items-start gap-3 ${isTaskClosed(event) ? 'opacity-60' : ''}`}>
                        {/* Color indicator / Task checkbox */}
                        {event.eventType === 'task' ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onToggleTaskStatus) {
                                onToggleTaskStatus(event.id, isTaskClosed(event) ? 'scheduled' : 'completed');
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                e.preventDefault();
                                if (onToggleTaskStatus) {
                                  onToggleTaskStatus(event.id, isTaskClosed(event) ? 'scheduled' : 'completed');
                                }
                              }
                            }}
                            className="flex-shrink-0 p-0.5 cursor-pointer"
                            title={isTaskClosed(event) ? 'Reopen task' : 'Complete task'}
                          >
                            {isTaskClosed(event) ? (
                              <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <CheckCircleIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        ) : (
                          <div className={`flex-shrink-0 w-1 h-full min-h-[3.5rem] rounded-full ${typeStyle.dot}`} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className={`text-sm font-medium text-gray-900 ${isTaskClosed(event) ? 'line-through text-gray-500' : ''}`}>
                                {event.title}
                              </h4>
                              <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                <ClockIcon className="h-3.5 w-3.5" />
                                {event.allDay ? (
                                  <span>All day</span>
                                ) : (
                                  <span>{format(startDate, 'h:mm a')} - {format(endDate, 'h:mm a')}</span>
                                )}
                              </div>
                              {(event.customerName || event.locationAddress) && (
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                  {event.customerName && (
                                    <span className="inline-flex items-center gap-1">
                                      <UserIcon className="h-3.5 w-3.5" />
                                      {event.customerName}
                                    </span>
                                  )}
                                  {event.locationAddress && (
                                    <span className="inline-flex items-center gap-1">
                                      <MapPinIcon className="h-3.5 w-3.5" />
                                      <span className="truncate max-w-[120px]">{event.locationAddress}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                              {statusBadge && (
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full mt-2 ${statusBadge.bg} ${statusBadge.text}`}>
                                  {statusBadge.label}
                                </span>
                              )}
                            </div>
                            {/* Overdue + Event type badge + Chevron - right aligned */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {overdue && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                  Overdue
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${typeStyle.bg} ${typeStyle.text}`}>
                                {event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}
                              </span>
                              <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {totalEvents > 0 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Show:</span>
              <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-0.5">
                {itemsPerPageOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleItemsPerPageChange(option.value)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      itemsPerPage === option.value
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Page info and navigation */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {isViewAll ? (
                  `Showing all ${totalEvents} ${totalEvents === 1 ? 'event' : 'events'}`
                ) : (
                  `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalEvents)}-${Math.min(currentPage * itemsPerPage, totalEvents)} of ${totalEvents}`
                )}
              </span>

              {/* Pagination buttons (hidden when viewing all) */}
              {!isViewAll && totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-600 min-w-[4rem] text-center">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventListView;
